import { forwardRef, useState } from 'react'
import { cn } from '../../utils/cn'
import PropTypes from 'prop-types'
import { Eye, EyeOff, AlertCircle, Check } from 'lucide-react'

const Input = forwardRef(({ 
  className, 
  type, 
  label,
  placeholder,
  error,
  success,
  helperText,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  variant = 'default',
  size = 'default',
  ...props 
}, ref) => {
  const [showPassword, setShowPassword] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  
  const isPassword = type === 'password'
  const inputType = isPassword && showPassword ? 'text' : type
  
  const inputVariants = {
    default: 'border-gray-200 focus:border-primary focus:ring-primary/20',
    error: 'border-red-300 focus:border-red-500 focus:ring-red-500/20',
    success: 'border-green-300 focus:border-green-500 focus:ring-green-500/20',
  }
  
  const sizeVariants = {
    sm: 'h-8 px-3 text-sm',
    default: 'h-10 px-3 text-sm',
    lg: 'h-12 px-4 text-base',
  }
  
  const currentVariant = error ? 'error' : success ? 'success' : variant
  
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      
      <div className="relative">
        {LeftIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <LeftIcon className="h-4 w-4 text-gray-400" />
          </div>
        )}
        
        <input
          type={inputType}
          ref={ref}
          placeholder={placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={cn(
            'flex w-full rounded-lg border bg-white text-gray-900 transition-all duration-200',
            'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50',
            sizeVariants[size],
            inputVariants[currentVariant],
            LeftIcon && 'pl-10',
            (RightIcon || isPassword || error || success) && 'pr-10',
            isFocused && 'shadow-sm',
            className
          )}
          {...props}
        />
        
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {error && (
            <AlertCircle className="h-4 w-4 text-red-500" />
          )}
          
          {success && !error && (
            <Check className="h-4 w-4 text-green-500" />
          )}
          
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          )}
          
          {RightIcon && !error && !success && !isPassword && (
            <RightIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>
      
      {(helperText || error) && (
        <p className={cn(
          'text-xs',
          error ? 'text-red-600' : 'text-gray-500'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  )
})

Input.displayName = 'Input'

Input.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  success: PropTypes.bool,
  helperText: PropTypes.string,
  leftIcon: PropTypes.elementType,
  rightIcon: PropTypes.elementType,
  variant: PropTypes.oneOf(['default', 'error', 'success']),
  size: PropTypes.oneOf(['sm', 'default', 'lg']),
}

export { Input }